import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import _ from 'lodash';

import roles from '../../common/constants/roles';
import { overviewConstants } from '../constants/appConstants';
import RoleRepository from '../repositories/roleRepository';
import { queryClient } from '../router/Root';
import { isCompanyAdmin, isSupportAdmin } from '../selectors/userSelectors';
import { useZustand } from '../zustandStore';
import type { RolesSlice, User } from '../zustandStore/slices';

import { rolesKeys, teamKeys, userKeys } from './query-keys';
import { useGetCurrentTeamQuery } from './teamQuery';
import type { Role } from './teamQuery';

const { companyAdmin: COMPANY_ADMIN, tradeshiftSupportAdmin: TRADESHIFT_SUPPORT_ADMIN } = roles;

export const useGetRolesV2Query = (role, tsAdmin) => {
	const { setRoles } = useZustand();
	return useQuery({
		queryKey: [rolesKeys.setRoles, roles, setRoles],
		queryFn: async () => {
			setRoles({ roles });
			return roles;
		},
		enabled: !role && !!tsAdmin,
	});
};

export const useGetTeamRolesV2Query = (role, tsAdmin) => {
	const { setRoles } = useZustand();
	return useQuery({
		queryKey: [rolesKeys.setRoles, roles, setRoles],
		queryFn: async () => {
			const rolesData = _.omit(roles, [
				'companyAdmin',
				'tradeshiftSupportAdmin',
				'getRoleById',
				'getRoleOptionsObject',
				'admin',
				'basic',
				'member',
				'finance',
				'support',
			]);
			setRoles({ roles: rolesData });
			return rolesData;
		},
		enabled: !role && !tsAdmin,
	});
};

export const useGetAdminRolesV2Query = role => {
	const { setRoles } = useZustand();
	return useQuery({
		queryKey: [rolesKeys.setRoles, role?.id, setRoles],
		queryFn: async () => {
			if (role?.id) {
				const rolesData = roles.getRoleOptionsObject(role?.id);
				setRoles({
					roles: rolesData as RolesSlice['role']['roles'],
				});
				return rolesData;
			} else {
				return {};
			}
		},
		enabled: Boolean(role?.id),
	});
};

export const useAddRoleToUserQuery = () => {
	return useMutation({
		mutationFn: RoleRepository.addRoleToUser,
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: [userKeys.getUsersByRoleId(overviewConstants.COMPANY_ADMIN)] });
			void queryClient.invalidateQueries({ queryKey: [userKeys.getUsersByRoleId(overviewConstants.TEAM_ADMIN)] });
		},
	});
};

type RemoveRoleFromUserInput = {
	userId: string;
	roleId: string;
	securityGroupId: string;
};

export const useRemoveRoleFromUserQuery = (roleId: string) => {
	const queryClient = useQueryClient();
	return useMutation<AxiosResponse, AxiosError, RemoveRoleFromUserInput>({
		mutationFn: (input: RemoveRoleFromUserInput) => RoleRepository.removeRoleFromUser(input),
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: [userKeys.getUsersByRoleId(roleId)],
			});
		},
	});
};

export const useUpdateUserRoleQuery = () => {
	const queryClient = useQueryClient();
	return useMutation<AxiosResponse, AxiosError, { userId: string; selectedRoleId: string; teamId: string }>({
		mutationFn: ({ userId, selectedRoleId, teamId }) => RoleRepository.updateUserRole(userId, selectedRoleId, teamId),
		onSuccess: async () => {
			void queryClient.invalidateQueries({
				queryKey: [teamKeys.getDecoratedUser],
			});
			void queryClient.invalidateQueries({
				queryKey: [teamKeys.getTeamUsers],
			});
		},
	});
};

export const getRoleV2 = key => {
	return roles[key];
};

const getRoleById = (id: Role['roleId']) =>
	_.find(roles, { id }) as { id: string; label: string; mappedTSRoleId: string };

export const useGetRoleForUserQuery = (user: User | undefined, teamId: string) => {
	const enabled = Boolean(user) && user?.roles && user?.roles.length > 0 && Boolean(teamId);
	return useQuery({
		queryKey: [rolesKeys.setRole, user?.userId, teamId, user?.roles],
		queryFn: () => {
			const currentRole = user?.roles.find(role => role.securityGroupId === teamId);
			return getRoleById(currentRole?.roleId || '') || { id: '' };
		},
		enabled: Boolean(enabled),
	});
};

export const useGetCurrentRoleForUserQuery = (user, teams, teamId) => {
	const { data: currentTeam } = useGetCurrentTeamQuery(teams, teamId);
	if (!user) {
		return { label: '' };
	}
	if (isCompanyAdmin(user?.roles)) {
		return COMPANY_ADMIN;
	} else if (isSupportAdmin(user?.roles)) {
		return TRADESHIFT_SUPPORT_ADMIN;
	} else {
		const effectiveTeamId = currentTeam?.id || teamId;
		const currentRole = user?.roles.find(role => role.securityGroupId === effectiveTeamId);
		return currentRole?.roleId ? getRoleById(currentRole.roleId) : { label: '' };
	}
};
